import axios from "axios";
import { createBrowserHistory } from "history";
import appTranslationInstance from "../config/i18n";
import { getEntryPointStoreState } from "../store/entryPointStore";
import { GetTenantId, GetToken } from "./helpers";

const history = createBrowserHistory();
const httpClient = axios.create({
  baseURL: `${import.meta.env.PUBLIC_APIGATEWAY_URL}`
});

const getFileNameFromResponse = (response) => {
  const contentDisposition = response.headers["content-disposition"];

  if (!contentDisposition) return null;

  const filename = contentDisposition
    .split(";")
    ?.map((item) => item.trim())
    ?.find((item) => item.includes("filename="))
    ?.replace("filename=", "")
    ?.replace(/"/g, "");

  return filename;
};

httpClient.interceptors.request.use(
  (request) => {
    request.headers.Authorization = `Bearer ${GetToken()}`;

    request.headers["Accept-Language"] = appTranslationInstance.language;
    request.headers["TenantId"] = GetTenantId();
    request.headers["X-Timezone"] =
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

httpClient.interceptors.response.use(
  (response) => {
    const contentDisposition = response.headers["content-disposition"];

    if (contentDisposition)
      response.filename = getFileNameFromResponse(response);

    return response;
  },
  async (error) => {
    const status = error?.response?.status;

    const pathname = window.location.pathname;

    const { firstTimeUser, firstTimeOrganization } = getEntryPointStoreState();

    // Check if we are on the 403 page to prevent an infinite loop.
    if (status === 403 && pathname !== "/duedates/403") {
      // NOTE(ivelez): Could not correctly redirect the user to the 403 page using react-router-dom.
      window.location.href = window.location.origin + "/duedates/403";
    }

    if (
      status === 500 ||
      status === 404 ||
      (status === 401 &&
        firstTimeUser === false &&
        firstTimeOrganization === false)
    ) {
      navigateToErrorPage(status);
      return;
    }

    if (status === 401) {
      const refreshToken = sessionStorage.getItem("refresh_token");
      await refreshToken(refreshToken);
    }

    return Promise.reject(error.response);
  }
);

const navigateToErrorPage = (status) => {
  const pathname = window.location.pathname;

  const errorPages = [
    "500",
    "404",
    "401",
    "403",
    "invalidlicense",
    "invalid-board-access"
  ];

  const isOnErrorPage = errorPages.some((page) => pathname.includes(page));

  if (isOnErrorPage) {
    return;
  }

  switch (status) {
    case 500:
      history.push("/duedates/500");
      break;
    case 404:
      history.push("/duedates/404");
      break;
    case 401:
      if (pathname !== "/duedates/401") {
        window.location.href = window.location.origin + "/duedates/401";
      }
      break;
    default:
      break;
  }
};

export const refreshToken = async (refreshToken) => {
  try {
    if (!refreshToken) {
      throw new Error("No refresh token");
    }

    const clientId = sessionStorage.getItem("client_id");
    const requestUrl = `${
      import.meta.env.REACT_APP_AUTH_AUTHORITY
    }connect/token`;

    const requestData = {
      grant_type: "refresh_token",
      client_id: clientId || import.meta.env.REACT_APP_AUTH_CLIENTID,
      refresh_token: refreshToken
    };

    const requestConfig = {
      headers: { "content-type": "application/x-www-form-urlencoded" }
    };

    const response = await axios.post(requestUrl, requestData, requestConfig);
    sessionStorage.setItem("token", response.data.access_token);
  } catch {
    sessionStorage.clear();
    window.location.href = window.location.origin;
  }
};

// HTTP methods for interacting with the API
export const get = (url, params = {}, config = {}) => {
  config.params = params;
  return httpClient.get(url, config);
};
export const post = (url, data, config = {}) =>
  httpClient.post(url, data, config);
export const postForm = (url, data, config = {}) =>
  httpClient.postForm(url, data, config);
export const patch = (url, data) => httpClient.patch(url, data);
export const put = (url, data, params = {}) =>
  httpClient.put(url, data, { params });
export const remove = (url, params = {}, config = {}) => {
  config.params = params;
  return httpClient.delete(url, config);
};
export const removeMultiple = (url, data) => httpClient.delete(url, { data });

export default httpClient;
